// src/pages/EditTicket.jsx
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import ReviewerSelect from "../components/ReviewerSelect";
import {
    INTANGIBLE_LEVEL_OPTIONS,
    getIntangibleAnnualAmount,
} from "../utils/financial_util";

// --------------------------------------------------
// Page constants
// --------------------------------------------------
const PAGE_STEP1 = "step1";
const PAGE_STEP2 = "step2";
const PAGE_STEP3 = "step3";
const PAGE_STEP5 = "step5";

// How many 5-Why questions we want in the chatbot
const MAX_ROOT_CAUSE_QUESTIONS = 5;
const MIN_ROOT_CAUSE_QUESTIONS = 3;

const ACTIONS_ENDPOINT = "/ai/generate-actions";

// Default direct-benefits rows
const INITIAL_DIRECT_ROWS = [
    {
        id: 1,
        description: "Reduced materials or supply costs",
        amount: "",
        frequency: "annual",
    },
    { id: 2, description: "Tooling or vendor fees", amount: "", frequency: "annual" },
    { id: 3, description: "Rework or scrap reduction", amount: "", frequency: "annual" },
    {
        id: 4,
        description: "Resource cost elimination",
        amount: "",
        frequency: "annual",
    },
];

// --------------------------------------------------
// API helper (uses axiosInstance, prefix: /ticket)
// --------------------------------------------------
async function apiPost(url, data) {
    const res = await axiosInstance.post(url, data);
    return res.data;
}

// Centralised payload builder for the backend
function buildTicketPayload({
    ticketType,
    description,
    fiveWhys,
    actionsTaken,
    proposedActions,
    notes,
    improvementPlan,
    furtherActionsRequired,
    costs,
    benefits,
    chatHistory,
    selectedReviewer,
    daysRequired,
}) {
    return {
        ticketType, // 'reactive' | 'proactive'

        // Only populated for reactive path
        reactiveDetails:
            ticketType === "reactive"
                ? {
                    description: description || "",
                    rootCauseChat: fiveWhys || "",
                }
                : null,
        actionsTaken: actionsTaken || "",
        proposedActions: proposedActions || "",
        notes: notes || "",
        improvementPlan: improvementPlan || "",
        furtherActionsRequired:
            ticketType === "proactive"
                ? true
                : (typeof furtherActionsRequired === "boolean"
                    ? furtherActionsRequired
                    : false),

        // Improvement project section (used for both reactive + proactive)
        improvementProject: {
            costs: {
                currency: costs.currency || "",
                labourHours: costs.labourHours || "",
                initialInvestment: costs.initialInvestment || "",
                annualMaintenance: costs.annualMaintenance || "",
            },
            benefits: {
                productivity: benefits.productivity, // or null
                direct: benefits.direct, // or null
                intangible: benefits.intangible, // or null
            },
        },

        createdBy:
            typeof window !== "undefined" ? localStorage.getItem("email") || "" : "",
        reviewer: selectedReviewer || null,

        // AI root cause metadata        chatHistory: chatHistory || [],
        daysRequired: daysRequired,
    };
}

function pageLabel(page) {
    switch (page) {
        case PAGE_STEP1:
            return "Describe what happened";
        case PAGE_STEP2:
            return "Root cause analysis";
        case PAGE_STEP3:
            return "Proposed actions";
        case PAGE_STEP5:
            return "Improvement project";
        default:
            return "";
    }
}

// Serialise chatbot messages into a single 5-Why text block
function serialiseRootCauseMessages(messages) {
    if (!messages || messages.length === 0) return "";

    let qIndex = 0;
    let aIndex = 0;

    return messages
        .map((msg) => {
            if (msg.sender === "ai") {
                qIndex += 1;
                return `Q${qIndex}: ${msg.text}`;
            }
            if (msg.sender === "user") {
                aIndex += 1;
                return `A${aIndex}: ${msg.text}`;
            }
            return msg.text;
        })
        .join("\n");
}

// Best-effort parser: turn legacy rootCause text into messages
function parseFiveWhysToMessages(text) {
    if (!text) return [];

    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const messages = [];

    lines.forEach((line, idx) => {
        const match = line.match(/^(Q|A)\d*:\s*(.*)$/i);
        if (match) {
            const type = match[1].toUpperCase();
            const content = match[2] || "";
            messages.push({
                id: `legacy-${idx}`,
                sender: type === "Q" ? "ai" : "user",
                text: content,
            });
        } else {
            messages.push({
                id: `legacy-${idx}`,
                sender: "user",
                text: line,
            });
        }
    });

    return messages;
}

// --------------------------------------------------
// Main component – Edit existing ticket
// --------------------------------------------------
export default function EditTicket() {
    const params = useParams();
    const navigate = useNavigate();
    const ticketIdParam = params.ticketId || params.id;

    // Ticket identity
    const [ticketId, setTicketId] = useState(ticketIdParam || null);    const [chatHistory, setChatHistory] = useState([]); // [{"role": "user" | "assistant", "content": prompts/responses}]
    const [status, setStatus] = useState(null);
    const [implementationStatus, setImplementationStatus] = useState(null);

    const [currentPage, setCurrentPage] = useState(PAGE_STEP1);
    const [ticketType, setTicketType] = useState(null); // 'reactive' | 'proactive'
    const [stepsReached, setStepsReached] = useState([]);

    const [errorMessage, setErrorMessage] = useState("");
    const [savingStep, setSavingStep] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    const [comment, setComment] = useState("");

    // Description
    const [description, setDescription] = useState("");

    // Root cause analysis (stored as serialised transcript for backend)
    const [fiveWhys, setFiveWhys] = useState("");
    // Chatbot state for root cause analysis
    const [rootCauseMessages, setRootCauseMessages] = useState([]); // [{id, sender, text}]
    const [rootCauseInput, setRootCauseInput] = useState("");
    const [rootCauseQuestionCount, setRootCauseQuestionCount] = useState(0);
    const [rootCauseLoading, setRootCauseLoading] = useState(false);

    // Proposed actions and improvement plan
    const [actionsTaken, setActionsTaken] = useState("");
    const [proposedActions, setProposedActions] = useState("");
    const [actionsFeedback, setActionsFeedback] = useState("");
    const [notes, setNotes] = useState("");
    const [improvementPlan, setImprovementPlan] = useState("");
    const [furtherActionsRequired, setFurtherActionsRequired] = useState(null);

    // Improvement project cost section
    const [currency, setCurrency] = useState("");
    const [currencyLocked, setCurrencyLocked] = useState(false);
    const [labourHours, setLabourHours] = useState("");
    const [initialInvestment, setInitialInvestment] = useState("");
    const [annualMaintenance, setAnnualMaintenance] = useState("");

    // Improvement project benefits toggles
    const [prodActive, setProdActive] = useState(false);
    const [directActive, setDirectActive] = useState(false);
    const [intActive, setIntActive] = useState(false);

    // Improvement project productivity fields
    const [prodFrequency, setProdFrequency] = useState("");
    const [prodFrequencyUnit, setProdFrequencyUnit] = useState("week");
    const [hoursSaved, setHoursSaved] = useState("");

    // Improvement project direct benefits table
    const [directRows, setDirectRows] = useState(INITIAL_DIRECT_ROWS);

    // Improvement project intangible benefit level
    const [intangibleLevel, setIntangibleLevel] = useState("");

    // reviewer list
    const [reviewers, setReviewers] = useState([]);
    const [selectedReviewer, setSelectedReviewer] = useState(null);
    const reviewerEmail =
        typeof selectedReviewer === "string"
            ? selectedReviewer
            : selectedReviewer?.email ?? "";

    const [daysRequired, setDaysRequired] = useState("");

    // get reviewer list on mount
    useEffect(() => {
        const fetchReviewers = async () => {
            try {
                const res = await axiosInstance.get("/user/users");
                setReviewers(res.data || []);
                // console.log("Fetched reviewers:", res.data || []);
            } catch (err) {
                console.error("Failed to fetch reviewers", err);
            }
        };
        fetchReviewers();
    }, []);

    // --------------------------------------------------
    // Load existing ticket
    // --------------------------------------------------
    useEffect(() => {
        if (!ticketIdParam) {
            setLoadError("No ticket ID was provided in the URL.");
            setLoading(false);
            return;
        }

        let cancelled = false;

        async function fetchTicket() {
            setLoading(true);
            setLoadError("");
            try {
                const res = await axiosInstance.get(`/ticket/${ticketIdParam}`);
                const data = res.data;
                if (!data || cancelled) return;

                setTicketId(data.ticketId);
                setTicketType(data.ticketType || null);
                setStatus(data.status || null);
                setImplementationStatus(data.implementationStatus || null);
                setSelectedReviewer(data.reviewedBy || null);
                setComment(data.comment || "");

                if (Array.isArray(data.stepsReached)) {
                    setStepsReached(data.stepsReached);
                } else if (data.stepsReached) {
                    setStepsReached([data.stepsReached]);
                } else {
                    setStepsReached([]);
                }

                setDescription(data.description || "");
                const rootCauseText = data.rootCause || "";
                setFiveWhys(rootCauseText);
                setActionsTaken(data.actionsTaken || "");
                setProposedActions(data.proposedActions || "");
                setNotes(data.notes || "");
                setImprovementPlan(data.improvementPlan || "");
                setFurtherActionsRequired(
                    typeof data.furtherActionsRequired === "boolean"
                        ? data.furtherActionsRequired
                        : null
                );

                // AI root cause IDs
                const chatHistoryFromDb = data.chatHistory || []
                setChatHistory(chatHistoryFromDb);

                // If no chat history but we have legacy text, try to show it as messages
                if (!chatHistoryFromDb && rootCauseText) {
                    const legacyMessages = parseFiveWhysToMessages(rootCauseText);
                    setRootCauseMessages(legacyMessages);
                    setRootCauseQuestionCount(
                        legacyMessages.filter((m) => m.sender === "ai").length
                    );
                }

                setCurrency(data.currency || "");
                setCurrencyLocked(Boolean(data.currency));

                setLabourHours(
                    data.labourHours !== null && data.labourHours !== undefined
                        ? String(data.labourHours)
                        : ""
                );
                setInitialInvestment(
                    data.initialInvestment !== null && data.initialInvestment !== undefined
                        ? String(data.initialInvestment)
                        : ""
                );
                setAnnualMaintenance(
                    data.annualMaintenance !== null &&
                        data.annualMaintenance !== undefined
                        ? String(data.annualMaintenance)
                        : ""
                );

                setProdActive(Boolean(data.prodActive));
                setDirectActive(Boolean(data.directActive));
                setIntActive(Boolean(data.intActive));

                setProdFrequency(
                    data.prodFrequency !== null && data.prodFrequency !== undefined
                        ? String(data.prodFrequency)
                        : ""
                );
                setProdFrequencyUnit(data.prodFrequencyUnit || "week");
                setHoursSaved(
                    data.prodHoursSaved !== null && data.prodHoursSaved !== undefined
                        ? String(data.prodHoursSaved)
                        : ""
                );

                const dbRows = data.directBenefits;
                if (Array.isArray(dbRows) && dbRows.length) {
                    const mapped = dbRows.map((row, idx) => ({
                        id: row.id ?? idx + 1,
                        description: row.description || "",
                        amount:
                            row.amount !== null && row.amount !== undefined
                                ? String(row.amount)
                                : "",
                        frequency: row.frequency || "annual",
                    }));
                    setDirectRows(mapped);
                } else {
                    setDirectRows(INITIAL_DIRECT_ROWS);
                }

                setIntangibleLevel(data.intangibleLevel || "");

                // Decide which page to start on
                let initialPage = PAGE_STEP1;
                if (data.ticketType === "proactive") {
                    initialPage = PAGE_STEP5;
                } else {
                    const last = data.lastStepReached || "";
                    if (last.startsWith("Step 5")) initialPage = PAGE_STEP5;
                    else if (last.startsWith("Step 4")) initialPage = PAGE_STEP5;
                    else if (last.startsWith("Step 3")) initialPage = PAGE_STEP3;
                    else if (last.startsWith("Step 2")) initialPage = PAGE_STEP2;
                    else initialPage = PAGE_STEP1;
                }
                setCurrentPage(initialPage);
            } catch (err) {
                console.error("Failed to load ticket", err);
                setLoadError("Failed to load ticket. Please refresh or try again.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchTicket();

        return () => {
            cancelled = true;
        };
    }, [ticketIdParam]);

    // --------------------------------------------------
    // Load AI history once we have a chat history
    // --------------------------------------------------
    useEffect(() => {
        if (!chatHistory) return;

        let cancelled = false;

        async function loadHistory() {
            try {

                // let rawHistory = res.data?.history ?? [];
                // console.log(
                //     "AI history for ticket",
                //     ticketId,
                //     rawHistory,
                //     "typeof:",
                //     typeof rawHistory
                // );

                let rawHistory = chatHistory;
                // Normalise rawHistory into an array
                let historyArray = [];

                if (Array.isArray(rawHistory)) {
                    historyArray = rawHistory;
                } else if (rawHistory && typeof rawHistory === "string") {
                    // Backend is probably returning a JSON string
                    try {
                        const parsed = JSON.parse(rawHistory);
                        if (Array.isArray(parsed)) {
                            historyArray = parsed;
                        } else if (parsed && Array.isArray(parsed.messages)) {
                            historyArray = parsed.messages;
                        } else if (parsed && Array.isArray(parsed.data)) {
                            historyArray = parsed.data;
                        } else {
                            // Single string, treat as one assistant message
                            historyArray = [
                                { role: "assistant", content: String(rawHistory) },
                            ];
                        }
                    } catch (e) {
                        console.warn("Could not parse history string, using as single msg:", rawHistory);
                        historyArray = [{ role: "assistant", content: String(rawHistory) }];
                    }
                } else if (rawHistory && typeof rawHistory === "object") {
                    // e.g. { messages: [...] } or { data: [...] }
                    if (Array.isArray(rawHistory.messages)) {
                        historyArray = rawHistory.messages;
                    } else if (Array.isArray(rawHistory.data)) {
                        historyArray = rawHistory.data;
                    } else {
                        historyArray = [];
                    }
                } else {
                    historyArray = [];
                }

                if (cancelled) return;

                const mapped = [...historyArray] // copy so we don't mutate the original
                    .map((item, idx) => {
                        const role =
                            typeof item === "string"
                                ? "assistant"
                                : item.role || "assistant";

                        let content;
                        if (typeof item === "string") {
                            content = item;
                        } else if (typeof item.content === "string") {
                            content = item.content;
                        } else if (Array.isArray(item.content)) {
                            content = item.content.map((p) => p.text || "").join(" ");
                        } else {
                            content = String(item.content ?? "");
                        }

                        return {
                            id: `${idx}-${role}`,
                            sender: role === "assistant" ? "ai" : "user",
                            text: content,
                        };
                    });

                setRootCauseMessages(mapped);
                setRootCauseQuestionCount(
                    mapped.filter((m) => m.sender === "ai").length
                );

                const fiveWhysText = serialiseRootCauseMessages(mapped);
                setFiveWhys(fiveWhysText);
            } catch (err) {
                if (cancelled) return;
                console.error("Failed to retrieve AI history", err);
            }
        }

        loadHistory();

        return () => {
            cancelled = true;
        };
    }, [chatHistory, ticketId]);


    // --------------------------------------------------
    // Utility helpers
    // --------------------------------------------------
    function addStepReached(label) {
        setStepsReached((prev) => (prev.includes(label) ? prev : [...prev, label]));
    }

    function buildCostsAndBenefits() {
        const costs = {
            currency,
            labourHours,
            initialInvestment,
            annualMaintenance,
        };

        const benefits = {
            productivity: prodActive
                ? {
                    frequency: prodFrequency,
                    unit: prodFrequencyUnit,
                    hoursSaved,
                }
                : null,
            direct: directActive ? directRows : null,
            intangible:
                intActive && intangibleLevel
                    ? {
                        active: true,
                        level: intangibleLevel,
                        amount: getIntangibleAnnualAmount(intangibleLevel),
                    }
                    : null,
        };

        return { costs, benefits };
    }

    // --------------------------------------------------
    // Currency lock
    // --------------------------------------------------
    function handleLockCurrency() {
        if (!currency) {
            toast.info("Please choose a currency first.");
            return;
        }
        if (currencyLocked) return;

        const ok = window.confirm(
            "Once locked, the currency cannot be changed unless the improvement project is reset. Continue?"
        );
        if (!ok) return;

        setCurrencyLocked(true);
        toast.success("Currency locked for this ticket.");
    }

    // --------------------------------------------------
    // Improvement project reset logic
    // --------------------------------------------------
    function handleResetStep5() {
        setCurrencyLocked(false);
        setCurrency("");

        setLabourHours("");
        setInitialInvestment("");
        setAnnualMaintenance("");

        setProdActive(false);
        setDirectActive(false);
        setIntActive(false);

        setProdFrequency("");
        setProdFrequencyUnit("week");
        setHoursSaved("");

        setDirectRows(INITIAL_DIRECT_ROWS);
        setIntangibleLevel("");
        setChatHistory([]);

        toast.info("Improvement project has been reset for this ticket.");
    }

    // --------------------------------------------------
    // Save as draft ("Save & continue later") – EDIT mode
    //  - POST /ticket/save-step
    //  - stays on this ticket, just updates draft
    // --------------------------------------------------
    async function handleSave(stepName) {
        setErrorMessage("");

        if (!ticketType) {
            toast.error("Ticket type is missing. Please contact support.");
            setErrorMessage("Ticket type is missing.");
            return;
        }

        let fiveWhysText = fiveWhys;
        if (chatHistory && rootCauseMessages.length) {
            fiveWhysText = serialiseRootCauseMessages(rootCauseMessages);
            setFiveWhys(fiveWhysText);
        }

        const { costs, benefits } = buildCostsAndBenefits();

        const payload = buildTicketPayload({
            ticketType,
            description,
            fiveWhys: fiveWhysText,
            actionsTaken,
            proposedActions,
            notes,
            improvementPlan,
            furtherActionsRequired,
            costs,
            benefits,            chatHistory,
            selectedReviewer,
            daysRequired,
        });

        const baseSteps = stepsReached || [];
        const mergedSteps = baseSteps.includes(stepName)
            ? baseSteps
            : [...baseSteps, stepName];

        try {
            setSavingStep(stepName);
            const result = await apiPost("/ticket/save-step", {
                ticketId,
                step: stepName,
                data: payload,
                stepsReached: mergedSteps,
            });

            if (result?.ticketId) {
                setTicketId(result.ticketId);
            }
            if (Array.isArray(result?.stepsReached)) {
                setStepsReached(result.stepsReached);
            } else {
                setStepsReached(mergedSteps);
            }
            if (result?.status) {
                setStatus(result.status);
            }

            toast.success("Draft ticket updated.");
            navigate("/draft-ticket");
        } catch (err) {
            console.error("Failed to save draft ticket", err);
            setErrorMessage("Failed to save ticket. Please try again.");
            toast.error("Failed to save ticket. Please try again.");
        } finally {
            setSavingStep(null);
        }
    }

    // --------------------------------------------------
    // Final submission – EDIT mode
    //  - POST /ticket/submit
    // --------------------------------------------------
    async function handleSubmitFinal(stepNameForHistory) {
        setErrorMessage("");

        if (!ticketType) {
            toast.error("Ticket type is missing. Please contact support.");
            setErrorMessage("Ticket type is missing.");
            return;
        }

        // If finishing from the improvement project, enforce some basic validation
        if (stepNameForHistory === "Step 5") {
            if (!currency.trim()) {
                toast.error(
                    "Please choose a currency for the improvement project before submitting."
                );
                return;
            }
        }

        let fiveWhysText = fiveWhys;        if (rootCauseMessages.length) {
            fiveWhysText = serialiseRootCauseMessages(rootCauseMessages);
            setFiveWhys(fiveWhysText);
        }

        const { costs, benefits } = buildCostsAndBenefits();

        const payload = buildTicketPayload({
            ticketType,
            description,
            fiveWhys: fiveWhysText,
            actionsTaken,
            proposedActions,
            notes,
            improvementPlan,
            furtherActionsRequired,
            costs,
            benefits,
            chatHistory,
            selectedReviewer,
            daysRequired,
        });

        const baseSteps = stepsReached || [];
        const mergedSteps = stepNameForHistory
            ? baseSteps.includes(stepNameForHistory)
                ? baseSteps
                : [...baseSteps, stepNameForHistory]
            : baseSteps;

        try {
            setSubmitting(true);
            const result = await apiPost("/ticket/submit", {
                ticketId,
                data: payload,
                stepsReached: mergedSteps,
            });

            if (result?.ticketId) {
                setTicketId(result.ticketId);
            }
            if (Array.isArray(result?.stepsReached)) {
                setStepsReached(result.stepsReached);
            }
            if (result?.status) {
                setStatus(result.status);
            }

            toast.success("Ticket submitted successfully.");
            navigate("/pending-ticket")
        } catch (err) {
            console.error("Failed to submit ticket", err);
            setErrorMessage("Failed to submit ticket. Please try again.");
            toast.error("Failed to submit ticket. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }

    // --------------------------------------------------
    // 5-Why chat: send answer in chatbot (root cause analysis)
    // --------------------------------------------------
    const handleSendRootCauseAnswer = async () => {
        const trimmed = (rootCauseInput || "").trim();
        if (!trimmed || rootCauseLoading) return;

        // Append user answer locally
        const userMsg = {
            id: Date.now(),
            sender: "user",
            text: trimmed,
        };
        setRootCauseMessages((prev) => [...prev, userMsg]);
        setRootCauseInput("");

        const reachedMax = rootCauseQuestionCount >= MAX_ROOT_CAUSE_QUESTIONS;

        try {
            setRootCauseLoading(true);

            const data = {
                description: trimmed,
                chatHistory,
            };

            const res = await axiosInstance.post("/ai/generate-question", data);
            const { question, history } = res.data || {};
            if (history && history !== chatHistory){
                setChatHistory(history);
            }

            if (!reachedMax && question) {
                const aiMsg = {
                    id: Date.now() + 1,
                    sender: "ai",
                    text: question,
                };
                setRootCauseMessages((prev) => [...prev, aiMsg]);
                setRootCauseQuestionCount((prev) => prev + 1);
            }
        } catch (err) {
            console.error("Failed to get next root cause question", err);
            toast.error(
                "Failed to get the next question. You can try again or save and continue later."
            );
        } finally {
            setRootCauseLoading(false);
        }
    };

    // --------------------------------------------------
    // Next handlers with validation
    // --------------------------------------------------
    async function handleNextFromStep1() {
        if (!description.trim()) {
            toast.warn("Please enter a short description before continuing.");
            return;
        }

        addStepReached("Step 1");

        // If chat history already exists, just move to root cause analysis (history will show)
        if (chatHistory) {
            setCurrentPage(PAGE_STEP2);
            return;
        }

        // Start a new AI 5-Why session (same as CreateTicket)
        toast.info(
            "Once the AI has generated the first question, will automatically move to the next step."
        );

        try {
            const data = {
                description,
                chatHistory,
            };

            const res = await axiosInstance.post("/ai/generate-question", data);
            const { question, history } = res.data || {};

            if (history) {
                setChatHistory(history);
            }

            if (question) {
                setRootCauseMessages([
                    {
                        id: Date.now(),
                        sender: "ai",
                        text: question,
                    },
                ]);
                setRootCauseQuestionCount(1);
            } else {
                toast.error(
                    "AI did not return a question. You can still continue and capture notes manually."
                );
                setRootCauseMessages([]);
                setRootCauseQuestionCount(0);
            }
        } catch (err) {
            console.error("Failed to generate first question", err);
            toast.error(
                "Failed to start the 5-Why chatbot. You can still continue and capture notes manually."
            );
            setRootCauseMessages([]);
            setRootCauseQuestionCount(0);
        }

        setCurrentPage(PAGE_STEP2);
    }

    const handleNextFromStep2 = async () => {
        // Chat-based path (AI thread present)
        if (chatHistory && rootCauseMessages.length) {
            const answerCount = rootCauseMessages.filter(
                (m) => m.sender === "user"
            ).length;

            if (answerCount < MIN_ROOT_CAUSE_QUESTIONS) {
                toast.warn(
                    `Please answer at least ${MIN_ROOT_CAUSE_QUESTIONS} questions before continuing.`
                );
                return;
            }

            addStepReached("Step 2");

            const fiveWhysText = serialiseRootCauseMessages(rootCauseMessages);
            setFiveWhys(fiveWhysText);

            toast.info("If the proposed actions have not been generated before, please wait a moment.");
            // Auto-submit to actions endpoint if proposedActions is still empty
            if (!proposedActions.trim()) {
                try {
                    const res = await axiosInstance.post(ACTIONS_ENDPOINT, {
                        fiveWhys: fiveWhysText,
                        description,
                    });

                    if (res?.data?.actions) {
                        setProposedActions(res.data.actions);
                    }
                } catch (err) {
                    console.warn(
                        "Actions endpoint not available or failed during edit (chat path). Proceeding without pre-filled proposed actions.",
                        err
                    );
                }
            }

            setCurrentPage(PAGE_STEP3);
            return;
        }

        // Legacy text-only path
        if (!fiveWhys.trim()) {
            toast.warn(
                "Please enter your 5-Why / root cause notes before continuing."
            );
            return;
        }

        toast.info("If the proposed actions have not been generated before, please wait a moment.");
        addStepReached("Step 2");

        // Auto-submit to actions endpoint if proposedActions is still empty (legacy text path)
        if (!proposedActions.trim()) {
            try {
                const res = await axiosInstance.post(ACTIONS_ENDPOINT, {
                    fiveWhys: fiveWhys,
                    description,
                });

                if (res?.data?.actions) {
                    setProposedActions(res.data.actions);
                }
            } catch (err) {
                console.warn(
                    "Actions endpoint not available or failed during edit (legacy path). Proceeding without pre-filled proposed actions.",
                    err
                );
            }
        }

        setCurrentPage(PAGE_STEP3);
    };

    // --------------------------------------------------
    // Render
    // --------------------------------------------------
    const isSaving = Boolean(savingStep);

    return (
        <div className="px-4 py-6 sm:px-6 lg:px-10">
            <div className="mx-auto max-w-6xl rounded-3xl bg-white/95 p-8 shadow-2xl ring-1 ring-slate-200">
                {/* Header */}
                <header className="mb-6 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                        <h1 className="text-2xl font-semibold text-slate-900">
                            Edit Ticket
                        </h1>
                        {/* dropdown menu to select reviewer */}
                        <ReviewerSelect
                            reviewers={reviewers}
                            onReviewerChange={(rev) => setSelectedReviewer(rev)}
                            value={reviewerEmail}
                        />

                        {/* days required to complete the ticket (input) */}
                        <div className="flex flex-col items-center mt-5">
                            <input
                                id="daysRequired"
                                type="number"
                                min={0}
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                                value={daysRequired}
                                onChange={(e) => setDaysRequired(e.target.value)}
                                placeholder="Estimated days to complete"
                            />
                            <p className="mt-1 text-xs text-slate-500">
                                Estimate the number of days required to complete the ticket.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-[11px] sm:text-xs">
                        {ticketId && (
                            <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 font-medium text-emerald-700 ring-1 ring-emerald-100">
                                Ticket ID: {ticketId}
                            </span>
                        )}
                        {ticketType && (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">
                                Path: {ticketType === "reactive" ? "Reactive" : "Proactive"}
                            </span>
                        )}
                        {status && (
                            <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 font-medium text-amber-700 ring-1 ring-amber-100">
                                Status: {status}
                            </span>
                        )}
                        <span className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 font-medium text-indigo-700 ring-1 ring-indigo-100">
                            {pageLabel(currentPage)}
                        </span>
                    </div>
                </header>

                {comment && (
                    <div className="mb-4 text-left rounded-lg border-l-4 border-red-600 bg-red-50 px-4 py-3 text-sm leading-relaxed text-slate-800">
                        <strong>Feedback:</strong>
                        <p>{comment}</p>
                    </div>
                )}

                {/* Load error */}
                {loadError && (
                    <div className="rounded-md border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-800">
                        {loadError}
                    </div>
                )}

                {/* Loading */}
                {loading && !loadError && (
                    <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
                        Loading ticket details...
                    </div>
                )}

                {/* Error banner (fallback) */}
                {errorMessage && !loading && !loadError && (
                    <div className="mb-4 rounded-md border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-800">
                        {errorMessage}
                    </div>
                )}

                {/* Page content (only when loaded) */}
                {!loading && !loadError && (
                    <>
                        {ticketType === "reactive" && currentPage === PAGE_STEP1 && (
                            <Step1Reactive
                                description={description}
                                onChangeDescription={setDescription}
                                actionsTaken={actionsTaken}
                                onChangeActionsTaken={setActionsTaken}
                                onNext={handleNextFromStep1}
                                onBack={() => setCurrentPage(PAGE_STEP1)} // no previous page in edit
                                onSave={() => handleSave("Step 1")}
                                saving={isSaving && savingStep === "Step 1"}
                            />
                        )}

                        {/* Root cause analysis - AI chat version (when chat history exists) */}
                        {ticketType === "reactive" &&
                            currentPage === PAGE_STEP2 &&
                            chatHistory && (
                                <Step2RootCauseChat
                                    messages={rootCauseMessages}
                                    inputValue={rootCauseInput}
                                    onInputChange={setRootCauseInput}
                                    onSend={handleSendRootCauseAnswer}
                                    loading={rootCauseLoading}
                                    onNext={handleNextFromStep2}
                                    onBack={() => setCurrentPage(PAGE_STEP1)}
                                    onSave={() => handleSave("Step 2")}
                                    saving={isSaving && savingStep === "Step 2"}
                                    maxQuestions={MAX_ROOT_CAUSE_QUESTIONS}
                                    minQuestions={MIN_ROOT_CAUSE_QUESTIONS}
                                />
                            )}

                        {/* Root cause analysis - legacy text-only version (no chat history) */}
                        {ticketType === "reactive" &&
                            currentPage === PAGE_STEP2 &&
                            !chatHistory && (
                                <Step2RootCause
                                    fiveWhys={fiveWhys}
                                    onChangeFiveWhys={setFiveWhys}
                                    onNext={handleNextFromStep2}
                                    onBack={() => setCurrentPage(PAGE_STEP1)}
                                    onSave={() => handleSave("Step 2")}
                                    saving={isSaving && savingStep === "Step 2"}
                                />
                            )}

                        {ticketType === "reactive" && currentPage === PAGE_STEP3 && (
                            <Step3ActionsTaken
                                actionsTaken={actionsTaken}
                                proposedActions={proposedActions}
                                improvementPlan={improvementPlan}
                                onChangeActionsTaken={setActionsTaken}
                                onChangeProposedActions={setProposedActions}
                                onChangeImprovementPlan={setImprovementPlan}
                                actionsFeedback={actionsFeedback}
                                onNoFurther={() => {
                                    addStepReached("Step 3 - No further action");
                                    setFurtherActionsRequired(false);
                                    handleSubmitFinal("Step 3 - No further action");
                                }}
                                onYesMore={() => {
                                    addStepReached("Step 3 - More actions required");
                                    setFurtherActionsRequired(true);
                                    setCurrentPage(PAGE_STEP5);
                                }}
                                onBack={() => setCurrentPage(PAGE_STEP2)}
                                onSave={() => handleSave("Step 3")}
                                saving={isSaving && savingStep === "Step 3"}
                            />
                        )}

                        {currentPage === PAGE_STEP5 && (
                            <Step5ImprovementProject
                                improvementPlan={improvementPlan}
                                onChangeImprovementPlan={setImprovementPlan}
                                improvementPlanLocked={
                                    String(status || "").trim().toLowerCase() === "completed"
                                }
                                currency={currency}
                                onCurrencyChange={setCurrency}
                                currencyLocked={currencyLocked}
                                onLockCurrency={handleLockCurrency}
                                labourHours={labourHours}
                                onLabourHoursChange={setLabourHours}
                                initialInvestment={initialInvestment}
                                onInitialInvestmentChange={setInitialInvestment}
                                annualMaintenance={annualMaintenance}
                                onAnnualMaintenanceChange={setAnnualMaintenance}
                                prodActive={prodActive}
                                setProdActive={setProdActive}
                                directActive={directActive}
                                setDirectActive={setDirectActive}
                                intActive={intActive}
                                setIntActive={setIntActive}
                                prodFrequency={prodFrequency}
                                setProdFrequency={setProdFrequency}
                                prodFrequencyUnit={prodFrequencyUnit}
                                setProdFrequencyUnit={setProdFrequencyUnit}
                                hoursSaved={hoursSaved}
                                setHoursSaved={setHoursSaved}
                                directRows={directRows}
                                onAddDirectRow={() =>
                                    setDirectRows((prev) => [
                                        ...prev,
                                        {
                                            id: Date.now(),
                                            description: "",
                                            amount: "",
                                            frequency: "annual",
                                        },
                                    ])
                                }
                                onRemoveDirectRow={(id) =>
                                    setDirectRows((prev) => prev.filter((r) => r.id !== id))
                                }
                                onUpdateDirectRow={(id, field, value) =>
                                    setDirectRows((prev) =>
                                        prev.map((r) =>
                                            r.id === id ? { ...r, [field]: value } : r
                                        )
                                    )
                                }
                                intangibleLevel={intangibleLevel}
                                setIntangibleLevel={setIntangibleLevel}
                                notes={notes}
                                onChangeNotes={setNotes}
                                notesLocked={
                                    String(implementationStatus || "").trim().toLowerCase() === "done"
                                }
                                onBack={() => {
                                    if (ticketType === "reactive") {
                                        setCurrentPage(PAGE_STEP3);
                                    }
                                }}
                                onFinish={() => handleSubmitFinal("Step 5")}
                                onReset={handleResetStep5}
                                onSave={() => handleSave("Step 5")}
                                saving={isSaving && savingStep === "Step 5"}
                                submitting={submitting}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

// --------------------------------------------------
// Shared UI elements
// --------------------------------------------------
function SystemBlock({ children }) {
    return (
        <div className="mb-4 text-left rounded-lg border-l-4 border-blue-600 bg-blue-50 px-4 py-3 text-sm leading-relaxed text-slate-800">
            {children}
        </div>
    );
}

function NavButtons({ children }) {
    return <div className="mt-6 flex flex-wrap gap-3">{children}</div>;
}

function PrimaryButton(props) {
    const { className, ...rest } = props;
    return (
        <button
            {...rest}
            className={
                "inline-flex items-center rounded-full bg-slate-900 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60 " +
                (className || "")
            }
        />
    );
}

function SecondaryButton(props) {
    const { className, ...rest } = props;
    return (
        <button
            {...rest}
            className={
                "inline-flex items-center rounded-full bg-slate-100 px-5 py-2.5 text-sm font-medium text-slate-900 shadow-sm transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60 " +
                (className || "")
            }
        />
    );
}

function OutlineButton(props) {
    const { className, ...rest } = props;
    return (
        <button
            {...rest}
            className={
                "inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-900 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 " +
                (className || "")
            }
        />
    );
}

// --------------------------------------------------
// Page components (same look-and-feel as CreateTicket)
// --------------------------------------------------
function Step1Reactive({
    description,
    onChangeDescription,
    actionsTaken,
    onChangeActionsTaken,
    onNext,
    onBack,
    onSave,
    saving,
}) {
    const disableNext = !description.trim();

    return (
        <section>
            <h2 className="mb-1 text-lg font-semibold text-slate-900">
                Describe what happened (Reactive only)
            </h2>
            <p className="mb-3 text-sm text-slate-500">
                This is only required for reactive tickets where a specific incident has
                occurred.
            </p>
            <SystemBlock>
                Thank you. Can you please explain, in 100 words or less, what happened and describe what interim actions have been taken?
            </SystemBlock>

            <label className="mb-2 block text-sm font-semibold text-slate-800">
                Please describe what happened
            </label>
            <textarea
                className="min-h-[140px] w-full resize-vertical rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                placeholder="Describe what happened..."
                value={description}
                onChange={(e) => onChangeDescription(e.target.value)}
            />

            <label className="mb-2 mt-5 block text-sm font-semibold text-slate-800">
                Interim actions that have been taken
            </label>
            <textarea
                className="min-h-[140px] w-full resize-vertical rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                placeholder="Interim actions that have been taken..."
                value={actionsTaken}
                onChange={(e) => onChangeActionsTaken(e.target.value)}
            />

            <NavButtons>
                <SecondaryButton onClick={onBack}>Back</SecondaryButton>
                <PrimaryButton onClick={onNext} disabled={disableNext}>
                    Next – Root cause analysis
                </PrimaryButton>
                <OutlineButton onClick={onSave} disabled={saving}>
                    {saving ? "Saving..." : "Save & continue later"}
                </OutlineButton>
            </NavButtons>
        </section>
    );
}

// New chatbot-style root cause analysis (for tickets with an AI thread)
function Step2RootCauseChat({
    messages,
    inputValue,
    onInputChange,
    onSend,
    loading,
    onNext,
    onBack,
    onSave,
    saving,
    maxQuestions,
    minQuestions,
}) {
    const totalQuestions = messages.filter((m) => m.sender === "ai").length;
    const totalAnswers = messages.filter((m) => m.sender === "user").length;
    const canProceed = totalAnswers >= minQuestions;

    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [messages]);

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSend();
        }
    };

    return (
        <section>
            <h2 className="mb-1 text-lg font-semibold text-slate-900">
                Root cause analysis (5-Why chatbot)
            </h2>
            <p className="mb-3 text-left text-sm text-slate-500">
                Answer up to {maxQuestions} short questions from the AI to capture your
                5-Why root cause analysis. After the final answer, you can move on to
                “Actions taken”.
            </p>
            <SystemBlock>
                This page represents the live 5-Why AI bot. Please respond to each
                question in your own words. The conversation will be stored with the
                ticket.
            </SystemBlock>

            {/* Chat window */}
            <div className="mt-4 h-96 w-full rounded-xl border border-slate-200 bg-slate-50/80 p-3 text-sm shadow-inner overflow-y-auto">
                {messages.length === 0 && (
                    <p className="text-xs text-slate-500">
                        The first AI question will appear here. If nothing appears, you can
                        still save this ticket and continue later.
                    </p>
                )}

                <div className="flex flex-col gap-2">
                    {messages.map((msg, idx) => {
                        const isAI = msg.sender === "ai";
                        return (
                            <div
                                key={msg.id ?? idx}
                                className={"flex " + (isAI ? "justify-start" : "justify-end")}
                            >
                                <div
                                    className={
                                        "max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed shadow-sm " +
                                        (isAI
                                            ? "bg-slate-100 text-slate-900"
                                            : "bg-slate-900 text-white")
                                    }
                                >
                                    <span className="mb-0.5 block text-xs font-semibold uppercase tracking-wide opacity-80">
                                        {isAI ? "AI" : "You"}
                                    </span>
                                    <p className="whitespace-pre-line text-left">{msg.text}</p>
                                </div>
                            </div>
                        );
                    })}
                    {/* anchor for auto-scroll */}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input area */}
            <div className="mt-2">
                <label className="mb-2 block text-sm font-semibold text-slate-800">
                    Type your answer
                </label>
                <textarea
                    className="min-h-[80px] w-full resize-vertical rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                    placeholder="Write a short answer to the current question..."
                    value={inputValue}
                    onChange={(e) => onInputChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading || totalAnswers >= maxQuestions}
                />
                <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                    <span className="text-sm text-slate-500">
                        {Math.min(totalAnswers, maxQuestions)} of {maxQuestions} answers
                        completed
                    </span>
                    <PrimaryButton
                        type="button"
                        onClick={onSend}
                        disabled={
                            loading || !inputValue.trim() || totalAnswers >= maxQuestions
                        }
                    >
                        {loading ? "Sending..." : "Send answer"}
                    </PrimaryButton>
                </div>
            </div>

            <NavButtons>
                <SecondaryButton onClick={onBack}>Back</SecondaryButton>
                <PrimaryButton onClick={onNext} disabled={!canProceed}>
                    Next – Actions taken
                </PrimaryButton>
                <OutlineButton onClick={onSave} disabled={saving}>
                    {saving ? "Saving..." : "Save & continue later"}
                </OutlineButton>
            </NavButtons>
        </section>
    );
}

// Legacy root cause analysis - text-only (no AI thread)
function Step2RootCause({
    fiveWhys,
    onChangeFiveWhys,
    onNext,
    onBack,
    onSave,
    saving,
}) {
    const disableNext = !fiveWhys.trim();

    return (
        <section>
            <h2 className="mb-1 text-lg font-semibold text-slate-900">
                Root cause analysis (5-Why bot)
            </h2>
            <p className="mb-3 text-sm text-slate-500">
                Capture your 5-Why analysis or paste the conversation with the 5-Why AI
                bot here.
            </p>
            <SystemBlock>
                This page represents the 5-Why AI bot. In the real app, this would be a
                chat conversation. For now, simply capture your analysis text below.
            </SystemBlock>

            <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                    <label className="mb-2 block text-sm font-semibold text-slate-800">
                        5 Whys chatbot / analysis
                    </label>
                    <textarea
                        className="min-h-[160px] w-full resize-vertical rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                        placeholder="Chatbot-style conversation or 5-Why reasoning..."
                        value={fiveWhys}
                        onChange={(e) => onChangeFiveWhys(e.target.value)}
                    />
                </div>
            </div>

            <NavButtons>
                <SecondaryButton onClick={onBack}>Back</SecondaryButton>
                <PrimaryButton onClick={onNext} disabled={disableNext}>
                    Next – Actions taken
                </PrimaryButton>
                <OutlineButton onClick={onSave} disabled={saving}>
                    {saving ? "Saving..." : "Save & continue later"}
                </OutlineButton>
            </NavButtons>
        </section>
    );
}

function Step3ActionsTaken({
    actionsTaken,
    proposedActions,
    improvementPlan,
    onChangeActionsTaken,
    onChangeProposedActions,
    onChangeImprovementPlan,
    actionsFeedback,
    onCheckActions,
    onNext,
    onNoFurther,
    onYesMore,
    onBack,
    onSave,
    saving,
}) {
    return (
        <section>
            <h2 className="mb-1 text-lg font-semibold text-slate-900">
                Proposed actions
            </h2>
            <p className="mb-3 text-sm text-slate-500">
                Review the AI-proposed actions, then decide whether more work is required.
            </p>
            <SystemBlock>
                The first field shows the AI-generated proposed actions. The second field is
                where you can describe your own improvement plan if more actions are
                required.
            </SystemBlock>

            <label className="mb-2 block text-sm font-semibold text-slate-800">
                Proposed actions
            </label>
            <textarea
                className="min-h-[200px] w-full resize-vertical rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                placeholder="AI-generated proposed actions will appear here."
                value={proposedActions}
                readOnly
            />

            <div className="mt-5">
                <label className="mb-2 block text-sm font-semibold text-slate-800">
                    Improvement Plan (Please describe your plan if you believe further actions are required)
                </label>
                <textarea
                    className="min-h-[180px] w-full resize-vertical rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                    placeholder="Please type actions that you believe should be taken...(This can be any of the AI countermeasures listed above, or any alternative actions that you think are better suited. If you believe no action is required, leave this blank)"
                    value={improvementPlan}
                    onChange={(e) => onChangeImprovementPlan(e.target.value)}
                />
            </div>

            {actionsFeedback && (
                <div className="mt-3 rounded-lg border-l-4 border-amber-400 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                    {actionsFeedback}
                </div>
            )}

            <NavButtons>
                <SecondaryButton onClick={onBack}>Back</SecondaryButton>
                <SecondaryButton onClick={onNoFurther}>
                    Close ticket (if no improvement plan)
                </SecondaryButton>
                <PrimaryButton onClick={onYesMore}>
                    Next - improvement plan costing
                </PrimaryButton>
                <OutlineButton onClick={onSave} disabled={saving}>
                    {saving ? "Saving..." : "Save & continue later"}
                </OutlineButton>
            </NavButtons>
        </section>
    );
}

function Step5ImprovementProject({
    currency,
    onCurrencyChange,
    currencyLocked,
    onLockCurrency,
    labourHours,
    onLabourHoursChange,
    initialInvestment,
    onInitialInvestmentChange,
    annualMaintenance,
    onAnnualMaintenanceChange,
    prodActive,
    setProdActive,
    directActive,
    setDirectActive,
    intActive,
    setIntActive,
    prodFrequency,
    setProdFrequency,
    prodFrequencyUnit,
    setProdFrequencyUnit,
    hoursSaved,
    setHoursSaved,
    directRows,
    onAddDirectRow,
    onRemoveDirectRow,
    onUpdateDirectRow,
    intangibleLevel,
    setIntangibleLevel,
    notes,
    onChangeNotes,
    notesLocked,
    improvementPlan,
    onChangeImprovementPlan,
    improvementPlanLocked,
    onBack,
    onFinish,
    onReset,
    onSave,
    saving,
    submitting,
}) {
    const [showResetConfirm, setShowResetConfirm] = useState(false);

    function toggleBenefit(type, value) {
        if (type === "prod") setProdActive(value);
        else if (type === "direct") setDirectActive(value);
        else if (type === "int") setIntActive(value);
    }

    const pill =
        "border border-slate-300 rounded-full px-4 py-1.5 text-xs transition-colors";
    const pillActive = pill + " bg-slate-900 text-white border-slate-900";

    const disabledFinal = submitting;

    return (
        <section>
            <h2 className="mb-1 text-lg font-semibold text-slate-900">
                Improvement project (Cost &amp; benefits)
            </h2>
            <p className="mb-3 text-sm text-slate-500">
                Capture the estimated cost and benefits of the improvement project
                linked to this ticket.
            </p>

            <SystemBlock>
                Your ideas are invaluable. Please complete this section so we can review
                the cost versus benefits of the improvement.
            </SystemBlock>

            <div className="mt-5">
                <label className="mb-2 block text-sm font-semibold text-slate-800">
                    Improvement Plan (Please describe your plan if you believe further actions are required)
                </label>
                <p className="mb-2 text-xs text-slate-500">
                    Please write what you are planning to implement.
                </p>
                <textarea
                    className="min-h-[140px] w-full resize-vertical rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                    placeholder="Please type actions that you believe should be taken...(This can be any of the AI countermeasures listed above, or any alternative actions that you think are better suited. If you believe no action is required, leave this blank)"
                    value={improvementPlan}
                    onChange={(e) => onChangeImprovementPlan(e.target.value)}
                    disabled={improvementPlanLocked}
                />
            </div>

            {/* 5.1 Currency */}
            <h3 className="mt-5 text-sm font-semibold text-slate-900">
                5.1 Currency (one-time choice)
            </h3>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                <label htmlFor="currency" className="text-sm text-slate-700">
                    <span className="font-semibold">Currency</span> for this improvement project:
                </label>
                <select
                    id="currency"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                    value={currency}
                    disabled={currencyLocked}
                    onChange={(e) => onCurrencyChange(e.target.value)}
                >
                    <option value="">Select...</option>
                    <option value="AUD">AUD</option>
                    <option value="CNY">CNY</option>
                    <option value="GBP">GBP</option>
                    <option value="ZAR">ZAR</option>
                </select>
                {/* <OutlineButton onClick={onLockCurrency} disabled={currencyLocked}>
                    {currencyLocked ? "Currency locked" : "Lock currency"}
                </OutlineButton> */}
            </div>
            {/* <p className="mt-1 text-xs text-slate-500">
                Once locked, the currency cannot be changed unless you reset the improvement project for
                this ticket.
            </p> */}

            {/* 5.2 Cost */}
            <h3 className="mt-6 text-sm font-semibold text-slate-900">
                Cost of improvement project
            </h3>

            <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-800">
                        Labour hours to implement
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="0.1"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                        value={labourHours}
                        onChange={(e) => onLabourHoursChange(e.target.value)}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                        Number of labour hours estimated to complete the project.
                    </p>
                </div>

                <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-800">
                        Initial investment to implement
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="1"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                        value={initialInvestment}
                        onChange={(e) => onInitialInvestmentChange(e.target.value)}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                        Up-front financial investment required.
                    </p>
                </div>

                <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-800">
                        Ongoing annual maintenance
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="1"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                        value={annualMaintenance}
                        onChange={(e) => onAnnualMaintenanceChange(e.target.value)}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                        Estimated annual cost of maintaining this improvement.
                    </p>
                </div>
            </div>

            {/* 5.3 Benefits */}
            <h3 className="mt-6 text-sm font-semibold text-slate-900">
                Benefits of improvement project
            </h3>

            {/* Productivity block */}
            <div className="mt-3">
                <div className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                    Productivity benefits?
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                    <button
                        type="button"
                        className={prodActive ? pillActive : pill}
                        onClick={() => toggleBenefit("prod", true)}
                    >
                        Yes
                    </button>
                    <button
                        type="button"
                        className={!prodActive ? pillActive : pill}
                        onClick={() => toggleBenefit("prod", false)}
                    >
                        No
                    </button>
                </div>

                {prodActive && (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-800">
                            Productivity benefits
                        </h4>
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                            <span>How often will this benefit occur?</span>
                            <input
                                type="number"
                                min="0"
                                className="w-20 rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                                value={prodFrequency}
                                onChange={(e) => setProdFrequency(e.target.value)}
                            />
                            <select
                                className="w-40 rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                                value={prodFrequencyUnit}
                                onChange={(e) => setProdFrequencyUnit(e.target.value)}
                            >
                                <option value="week">per week</option>
                                <option value="month">per month</option>
                                <option value="year">per annum</option>
                            </select>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                            <span>Hours saved per occurrence</span>
                            <input
                                type="number"
                                min="0"
                                step="0.1"
                                className="w-28 rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                                value={hoursSaved}
                                onChange={(e) => setHoursSaved(e.target.value)}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Direct block */}
            <div className="mt-5">
                <div className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                    Direct financial benefits?
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                    <button
                        type="button"
                        className={directActive ? pillActive : pill}
                        onClick={() => toggleBenefit("direct", true)}
                    >
                        Yes
                    </button>
                    <button
                        type="button"
                        className={!directActive ? pillActive : pill}
                        onClick={() => toggleBenefit("direct", false)}
                    >
                        No
                    </button>
                </div>

                {directActive && (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-800">
                            Direct financial benefits
                        </h4>
                        <p className="mb-3 text-xs text-slate-500">
                            Estimate annual or monthly financial benefits. You can add or
                            remove rows.
                        </p>

                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse text-xs">
                                <thead>
                                    <tr className="bg-slate-100">
                                        <th className="border border-slate-200 px-2 py-1 text-left">
                                            Description
                                        </th>
                                        <th className="border border-slate-200 px-2 py-1 text-left">
                                            Amount
                                        </th>
                                        <th className="border border-slate-200 px-2 py-1 text-left">
                                            Frequency
                                        </th>
                                        <th className="border border-slate-200 px-2 py-1 text-center">
                                            Remove
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {directRows.map((row) => (
                                        <tr key={row.id}>
                                            <td className="border border-slate-200 px-2 py-1">
                                                <input
                                                    type="text"
                                                    className="w-full rounded-md border border-slate-300 px-2 py-[4px] text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                                                    value={row.description}
                                                    onChange={(e) =>
                                                        onUpdateDirectRow(
                                                            row.id,
                                                            "description",
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Other benefit..."
                                                />
                                            </td>
                                            <td className="border border-slate-200 px-2 py-1">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="1"
                                                    className="w-full rounded-md border border-slate-300 px-2 py-[4px] text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                                                    value={row.amount}
                                                    onChange={(e) =>
                                                        onUpdateDirectRow(
                                                            row.id,
                                                            "amount",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td className="border border-slate-200 px-2 py-1">
                                                <select
                                                    className="w-full rounded-md border border-slate-300 px-2 py-[4px] text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                                                    value={row.frequency}
                                                    onChange={(e) =>
                                                        onUpdateDirectRow(
                                                            row.id,
                                                            "frequency",
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <option value="annual">Annual</option>
                                                    <option value="monthly">Monthly</option>
                                                </select>
                                            </td>
                                            <td className="border border-slate-200 px-2 py-1 text-center">
                                                <OutlineButton
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        onRemoveDirectRow(row.id);
                                                    }}
                                                >
                                                    X
                                                </OutlineButton>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <OutlineButton onClick={onAddDirectRow} className="mt-3">
                            Add row
                        </OutlineButton>
                    </div>
                )}
            </div>

            {/* Intangible block */}
            <div className="mt-5">
                <div className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                    Intangible / indirect benefits?
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                    <button
                        type="button"
                        className={intActive ? pillActive : pill}
                        onClick={() => toggleBenefit("int", true)}
                    >
                        Yes
                    </button>
                    <button
                        type="button"
                        className={!intActive ? pillActive : pill}
                        onClick={() => toggleBenefit("int", false)}
                    >
                        No
                    </button>
                </div>

                {intActive && (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-800">
                            Intangible / indirect benefits
                        </h4>
                        <ul className="mb-3 space-y-2 text-xs text-slate-600 text-left">
                            <li>
                                <span className="font-semibold text-slate-800">Extreme</span> –
                                Major improvement to CiiLOCK’s reputation or key relationships,
                                or prevents serious damage.
                                <div className="mt-0.5 italic text-slate-500">
                                    Example: Prevents loss of a major customer or significant
                                    reputational harm.
                                </div>
                            </li>
                            <li>
                                <span className="font-semibold text-slate-800">Very High</span>{" "}
                                – Significant improvement to CiiLOCK’s image or important
                                relationships, or prevents substantial damage.
                                <div className="mt-0.5 italic text-slate-500">
                                    Example: Resolves a recurring issue affecting an important
                                    customer.
                                </div>
                            </li>
                            <li>
                                <span className="font-semibold text-slate-800">High</span> –
                                Clear improvement to CiiLOCK’s image or relationships, or
                                meaningfully reduces potential damage.
                                <div className="mt-0.5 italic text-slate-500">
                                    Example: Removes repeated customer confusion or
                                    frustration.
                                </div>
                            </li>
                            <li>
                                <span className="font-semibold text-slate-800">Medium</span> –
                                Noticeable improvement to CiiLOCK’s image or relationships.
                                <div className="mt-0.5 italic text-slate-500">
                                    Example: Improves the clarity or professionalism of customer
                                    communication.
                                </div>
                            </li>
                            <li>
                                <span className="font-semibold text-slate-800">Low</span> –
                                Minor improvement to CiiLOCK’s image or relationships.
                                <div className="mt-0.5 italic text-slate-500">
                                    Example: Corrects a small presentation or communication
                                    issue.
                                </div>
                            </li>
                        </ul>

                        <div className="space-y-2 text-sm text-slate-800">
                            {INTANGIBLE_LEVEL_OPTIONS.map(({ label, amount }) => (
                                <label key={label} className="flex items-center gap-2">
                                    <input
                                        type="radio"
                                        name="intangible"
                                        value={label}
                                        checked={intangibleLevel === label}
                                        onChange={() => setIntangibleLevel(label)}
                                    />
                                    <span>
                                        {label}{" "}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-6">
                <label className="mb-2 block text-sm font-semibold text-slate-800">
                    Notes
                </label>
                <p className="mb-2 text-xs text-slate-500">
                    Please include reason for the ticket, and any actions taken. You can use this section as a note-taker.
                </p>
                <textarea
                    className="min-h-[140px] w-full resize-vertical rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                    placeholder="Please include reason for the ticket, and any actions taken. You can use this section as a note-taker."
                    value={notes}
                    onChange={(e) => onChangeNotes(e.target.value)}
                    disabled={notesLocked}
                />
            </div>

            <NavButtons>
                <SecondaryButton onClick={onBack} disabled={submitting}>
                    Back
                </SecondaryButton>
                <PrimaryButton onClick={onFinish} disabled={disabledFinal}>
                    {submitting ? "Submitting..." : "Finish & submit ticket"}
                </PrimaryButton>
                <OutlineButton
                    onClick={() => setShowResetConfirm(true)}
                    disabled={submitting}
                >
                    Reset improvement project
                </OutlineButton>
                <OutlineButton onClick={onSave} disabled={saving || submitting}>
                    {saving ? "Saving..." : "Save & continue later"}
                </OutlineButton>
            </NavButtons>

            {/* Custom reset confirmation modal */}
            {showResetConfirm && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl ring-1 ring-slate-200">
                        <h3 className="text-base font-semibold text-slate-900">
                            Reset improvement project?
                        </h3>
                        <p className="mt-2 text-sm text-slate-600">
                            This will clear all cost and benefit fields in the improvement project for this ticket. This action cannot be undone for this ticket.
                        </p>
                        <div className="mt-5 flex justify-end gap-3">
                            <SecondaryButton onClick={() => setShowResetConfirm(false)}>
                                Cancel
                            </SecondaryButton>
                            <PrimaryButton
                                onClick={() => {
                                    setShowResetConfirm(false);
                                    onReset();
                                }}
                            >
                                Yes, reset improvement project
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}







